import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, FileText } from "lucide-react";
import PrivacyBadge from "@/components/PrivacyBadge";
import { useAuth } from "@/context/AuthContext";
import { tr } from "@/lib/i18n";
import WhatNextPaywall from "@/components/WhatNextPaywall";

export default function NewScene() {
  const [title, setTitle] = useState("");
  const [brief, setBrief] = useState("");
  const [myChar, setMyChar] = useState("");
  const [charGender, setCharGender] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [auditionCtx, setAuditionCtx] = useState(null);
  const [scenePaywallOpen, setScenePaywallOpen] = useState(false);
  const navigate = useNavigate();
  const { refreshAccess, lang } = useAuth();
  const t = (k) => tr(k, lang);
  const [sceneLang, setSceneLang] = useState((lang || "pl").toLowerCase() === "en" ? "en" : "pl");

  // Pre-fill from CRM audition context (set in AuditionDetail.workWithLeo)
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("leo_audition_context");
      if (!raw) return;
      const ctx = JSON.parse(raw);
      setAuditionCtx(ctx);
      if (ctx.title) setTitle(ctx.title);
      if (ctx.my_character) setMyChar(ctx.my_character);
      if (ctx.project_brief) setBrief(ctx.project_brief);
      if (ctx.scene_text) setText(ctx.scene_text);
    } catch {}
  }, []);

  const uploadPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/pdf/extract", fd, { headers: {"Content-Type": "multipart/form-data"}});
      setText(r.data.text || "");
      if (!title) setTitle(file.name.replace(/\.pdf$/i, ""));
      toast.success(t("new.pdf_ok"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("new.pdf_fail"));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async (e, goTo = "analysis") => {
    if (e && e.preventDefault) e.preventDefault();
    if (text.trim().length < 20) {
      toast.error(t("new.short_text"));
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/scenes", {
        title: title.trim(),
        scene_text: text,
        project_brief: brief,
        my_character: myChar,
        character_gender: charGender,
        scene_language: sceneLang,
      });
      // Link back to audition scene if we came from CRM
      if (auditionCtx?.audition_id && auditionCtx?.audition_scene_id) {
        try {
          await api.patch(
            `/crm/auditions/${auditionCtx.audition_id}/scenes/${auditionCtx.audition_scene_id}`,
            { linked_scene_id: r.data.scene_id, status: "in_progress" }
          );
        } catch {}
        sessionStorage.removeItem("leo_audition_context");
      }
      await refreshAccess();
      toast.success(t("new.saved"));
      if (goTo === "rehearsal") {
        navigate(`/scenes/${r.data.scene_id}?tab=rehearsal&autoparse=1`);
      } else {
        navigate(`/scenes/${r.data.scene_id}?run=analysis`);
      }
    } catch (err) {
      if (err?.response?.status === 402) {
        setScenePaywallOpen(true);
      } else {
        toast.error(err?.response?.data?.detail || t("new.save_fail"));
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl">
      <div className="mono text-[11px] tracking-[0.4em] text-amber-500 uppercase">{t("new.eyebrow")}</div>
      <h1 className="serif text-5xl text-stone-100 mt-2">{t("new.title")}</h1>
      <p className="text-stone-400 mt-2">{t("new.subtitle")}</p>

      <form onSubmit={submit} className="mt-8 space-y-5">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="label-cap">{t("new.scene_title")}</label>
            <input data-testid="scene-title" required value={title} onChange={(e)=>setTitle(e.target.value)} className="script-input mt-1" placeholder={t("new.scene_title_ph")}/>
          </div>
          <div>
            <label className="label-cap">{t("new.my_character")}</label>
            <input data-testid="scene-character" value={myChar} onChange={(e)=>setMyChar(e.target.value)} className="script-input mt-1" placeholder={t("new.my_character_ph")}/>
            <select data-testid="scene-character-gender" value={charGender} onChange={(e)=>setCharGender(e.target.value)} className="script-input mt-2">
              <option value="">{lang === "pl" ? "Płeć postaci (wykryj z tekstu)" : "Character gender (detect from text)"}</option>
              <option value="male">{lang === "pl" ? "Mężczyzna" : "Male"}</option>
              <option value="female">{lang === "pl" ? "Kobieta" : "Female"}</option>
            </select>
          </div>
        </div>

        <div>
          <label className="label-cap">{t("new.scene_language")}</label>
          <select
            data-testid="scene-language"
            value={sceneLang}
            onChange={(e)=>setSceneLang(e.target.value)}
            className="script-input mt-1 mono text-sm"
          >
            <option value="pl">PL</option>
            <option value="en">EN</option>
            <option value="ja">JA</option>
          </select>
        </div>

        <div>
          <label className="label-cap">{t("new.brief")}</label>
          <textarea data-testid="scene-brief" value={brief} onChange={(e)=>setBrief(e.target.value)} rows={3} className="script-input mt-1 mono text-sm" placeholder={t("new.brief_ph")}/>
        </div>

        <div className="paper p-5">
          <div className="flex items-center justify-between gap-3">
            <div className="label-cap flex items-center gap-2"><FileText size={12}/> {t("new.scene_text_label")}</div>
            <div className="flex items-center gap-3">
              <PrivacyBadge />
              <label className={`ghost-btn !py-2 !text-xs cursor-pointer ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
                <UploadCloud size={14}/> {uploading ? t("new.reading") : t("new.upload_pdf")}
                <input data-testid="scene-pdf-input" type="file" accept="application/pdf,image/jpeg,image/png" className="hidden" onChange={uploadPdf}/>
              </label>
            </div>
          </div>
          <div className="mono text-[10px] text-stone-500 mt-1 text-right">{t("new.upload_formats")}</div>
          <textarea
            data-testid="scene-text"
            value={text}
            onChange={(e)=>setText(e.target.value)}
            rows={14}
            className="script-input mt-3 mono text-[14px] leading-relaxed"
            placeholder="INT. KAWIARNIA — WIECZÓR&#10;&#10;MARTA&#10;   Nie mogę już dłużej udawać.&#10;&#10;JAN&#10;   To dlaczego przyszłaś?"
          />
          <div className="mono text-[11px] text-stone-500 mt-2">{text.length} {t("new.chars")}</div>
        </div>

        <div className="flex gap-3 flex-wrap">
          <button data-testid="scene-save-btn" type="submit" disabled={busy} className="pill-btn">
            {busy ? t("new.saving") : t("new.save_run")}
          </button>
          <button
            data-testid="scene-save-rehearsal-btn"
            type="button"
            onClick={(e)=>submit(e, "rehearsal")}
            disabled={busy}
            className="pill-btn"
          >
            {busy ? t("new.saving") : t("new.save_go_rehearsal")}
          </button>
          <button type="button" onClick={()=>navigate(-1)} className="ghost-btn">{t("action.cancel")}</button>
        </div>
      </form>

      <WhatNextPaywall
        open={scenePaywallOpen}
        kind="scene"
        lang={lang}
        onClose={() => setScenePaywallOpen(false)}
        onSeeAllPlans={() => { setScenePaywallOpen(false); navigate("/pricing"); }}
      />
    </div>
  );
}
